import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import {
  EActionAttribute,
  EColor,
  EPosition,
  ETypographyVariant,
  EZIndexName,
  TooltipSmallPositionProps,
} from '@core/type';

import { BaseBoxProps, Box } from '../layout';
import { Typography } from '../typography';
import { TooltipPositionProps, TooltipSmallDescriptionProps } from './interface-tooltip';

const SPACE_BETWEEN = 8;
const TOOLTIP_POSITION = `calc(100% + ${SPACE_BETWEEN}px)`;

const getStaticPlacement = (placement: TooltipSmallPositionProps) => {
  if (placement === EPosition.LEFT) {
    return { right: TOOLTIP_POSITION };
  }

  if (placement === EPosition.RIGHT) {
    return { left: TOOLTIP_POSITION };
  }

  return { left: TOOLTIP_POSITION };
};

export const TooltipSmallDescription = ({
  content,
  children,
  placement,
  top,
  ...props
}: TooltipSmallDescriptionProps & BaseBoxProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [positionProps, setPositionProps] = useState<TooltipPositionProps>(
    getStaticPlacement(placement),
  );

  const tooltipSettings = () => {
    const screenWidth = window.innerWidth;
    const { width: tooltipWidth } = tooltipRef.current.getBoundingClientRect();
    const { left: containerLeft, width: containerWidth } =
      containerRef.current.getBoundingClientRect();

    if (containerLeft + containerWidth + SPACE_BETWEEN + tooltipWidth > screenWidth) {
      setPositionProps({ right: TOOLTIP_POSITION });
    }
  };

  useEffect(() => {
    if (!placement) {
      tooltipSettings();
      window.addEventListener('resize', tooltipSettings);
    }

    return () => {
      window.removeEventListener('resize', tooltipSettings);
    };
  }, []);

  return (
    <StyledChildrenContainer ref={containerRef} position={'relative'} cursor={'pointer'} {...props}>
      {children}
      <StyledTooltipContainer
        {...positionProps}
        ref={tooltipRef}
        position={'absolute'}
        zIndex={EZIndexName.TOOLTIP}
        backgroundColor={EColor.SECONDARY}
        borderColor={{ commonType: EColor.GRAY, intensity: EColor.A400 }}
        borderWidth={'1px'}
        boxShadow={EColor.ELEVATION_2}
        top={top}
      >
        <Typography
          as={EActionAttribute.SPAN}
          variant={ETypographyVariant.CAPTION1}
          color={EColor.PRIMARY}
        >
          {content}
        </Typography>
      </StyledTooltipContainer>
    </StyledChildrenContainer>
  );
};

const StyledChildrenContainer = styled(Box)<BaseBoxProps>(() => {
  return css`
    width: fit-content;

    &:hover ${StyledTooltipContainer.toString()} {
      opacity: 1;
    }
  `;
});

const StyledTooltipContainer = styled(Box)(() => {
  return css`
    opacity: 0;
    top: -9px;
    padding: 2px 8px 4px;
    max-width: 300px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    border-radius: 8px;
    transition: 0.3s;
    pointer-events: none;
  `;
});
